import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Building2, AlertTriangle, FileText, Bell, BarChart3,
  TrendingUp, CheckCircle2, XCircle, Shield,
} from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

const BRAND = "#7B2D3F";
const COLORS = [BRAND, "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444"];

const PERIOD_OPTIONS = [
  { value: "7d", label: "Últims 7 dies" },
  { value: "30d", label: "Últims 30 dies" },
  { value: "90d", label: "Últims 90 dies" },
];

export default function Analytics() {
  const { venues, userRole, loading: venueLoading } = useVenue();
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedVenueId, setSelectedVenueId] = useState("all");

  useEffect(() => {
    if (userRole === "superadmin" && venues.length > 0) loadData();
  }, [userRole, venues, period]);

  const loadData = async () => {
    setLoading(true);
    const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
    const startDate = moment().subtract(days, "days").format("YYYY-MM-DD");
    const today = moment().format("YYYY-MM-DD");

    // Fetch all data in parallel
    const [allTemps, allCleaning, allIncidents, allAlerts, allDocs, allVerif] = await Promise.all([
      hosys.entities.TemperatureRecord.list(),
      hosys.entities.CleaningRecord.list(),
      hosys.entities.Incident.list(),
      hosys.entities.Alert.list(),
      hosys.entities.Document.list(),
      hosys.entities.Verification.list(),
    ]);

    // Filter by period
    const temps = allTemps.filter(r => r.date >= startDate);
    const cleaning = allCleaning.filter(r => r.date >= startDate);
    const incidents = allIncidents.filter(r => r.date >= startDate);
    const alerts = allAlerts.filter(r => (r.created_date || "").slice(0, 10) >= startDate);
    const verif = allVerif.filter(r => r.planned_date >= startDate);

    // Venue activity (has any record in period)
    const activeVenueIds = new Set([
      ...temps.map(r => r.venue_id),
      ...cleaning.map(r => r.venue_id),
      ...incidents.map(r => r.venue_id),
    ]);

    // Venues with open incidents
    const openIncidentVenueIds = new Set(
      allIncidents.filter(i => i.status !== "closed").map(i => i.venue_id)
    );

    // Venues with expired docs
    const expiredDocVenueIds = new Set(
      allDocs.filter(d => d.expiry_date && moment(d.expiry_date).isBefore(moment(), "day")).map(d => d.venue_id)
    );

    // Venues with critical active alerts
    const criticalAlertVenueIds = new Set(
      allAlerts.filter(a => a.status === "active" && a.severity === "critical").map(a => a.venue_id)
    );

    // === Daily activity chart (registres per dia) ===
    const dailyMap = {};
    for (let i = 0; i < days; i++) {
      const d = moment().subtract(i, "days").format("YYYY-MM-DD");
      dailyMap[d] = { date: d, label: moment(d).format("D MMM"), temps: 0, cleaning: 0, incidents: 0 };
    }
    temps.forEach(r => { if (dailyMap[r.date]) dailyMap[r.date].temps++; });
    cleaning.forEach(r => { if (dailyMap[r.date]) dailyMap[r.date].cleaning++; });
    incidents.forEach(r => { if (dailyMap[r.date]) dailyMap[r.date].incidents++; });
    const dailyData = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));

    // === Incidents by category ===
    const incidentByCategory = {};
    allIncidents.filter(i => i.date >= startDate).forEach(i => {
      incidentByCategory[i.category] = (incidentByCategory[i.category] || 0) + 1;
    });
    const incidentCategoryData = Object.entries(incidentByCategory).map(([k, v]) => ({
      name: INCIDENT_CATEGORY_LABELS[k] || k, value: v,
    }));

    // === Incidents by severity ===
    const bySeverity = { low: 0, medium: 0, high: 0, critical: 0 };
    allIncidents.filter(i => i.date >= startDate).forEach(i => {
      if (bySeverity[i.severity] !== undefined) bySeverity[i.severity]++;
    });
    const severityData = Object.entries(bySeverity).map(([k, v]) => ({
      name: SEVERITY_LABELS[k], value: v, color: SEVERITY_COLORS[k],
    }));

    // === Compliance per venue ===
    const venueComplianceData = venues.map(venue => {
      const vVerif = verif.filter(v => v.venue_id === venue.id);
      const correct = vVerif.filter(v => v.status === "correct").length;
      const evaluated = vVerif.filter(v => v.status === "correct" || v.status === "incorrect").length;
      const tempVerif = temps.filter(r => r.venue_id === venue.id);
      const tempCorrect = tempVerif.filter(r => r.status === "correct").length;
      const cleanVerif = cleaning.filter(r => r.venue_id === venue.id);
      const cleanDone = cleanVerif.filter(r => r.completed).length;
      const openInc = allIncidents.filter(i => i.venue_id === venue.id && i.status !== "closed").length;
      return {
        name: venue.name.length > 14 ? venue.name.slice(0, 14) + "…" : venue.name,
        fullName: venue.name,
        compliment: evaluated > 0 ? Math.round((correct / evaluated) * 100) : null,
        temps: tempVerif.length,
        tempCompliment: tempVerif.length > 0 ? Math.round((tempCorrect / tempVerif.length) * 100) : null,
        neteja: cleanVerif.length > 0 ? Math.round((cleanDone / cleanVerif.length) * 100) : null,
        incidencies: openInc,
        alertes: allAlerts.filter(a => a.venue_id === venue.id && a.status === "active").length,
      };
    }).filter(v => v.compliment !== null || v.temps > 0);

    // === Alert trend ===
    const alertDailyMap = {};
    for (let i = 0; i < Math.min(days, 30); i++) {
      const d = moment().subtract(i, "days").format("YYYY-MM-DD");
      alertDailyMap[d] = { date: d, label: moment(d).format("D MMM"), alertes: 0 };
    }
    alerts.forEach(a => {
      const d = (a.created_date || "").slice(0, 10);
      if (alertDailyMap[d]) alertDailyMap[d].alertes++;
    });
    const alertTrend = Object.values(alertDailyMap).sort((a, b) => a.date.localeCompare(b.date));

    // === Global KPIs ===
    const totalTemps = temps.length;
    const correctTemps = temps.filter(r => r.status === "correct").length;
    const totalCleaning = cleaning.length;
    const doneCleaning = cleaning.filter(r => r.completed).length;
    const totalVerif = verif.length;
    const correctVerif = verif.filter(v => v.status === "correct").length;
    const evaluatedVerif = verif.filter(v => v.status === "correct" || v.status === "incorrect").length;
    const totalOpenIncidents = allIncidents.filter(i => i.status !== "closed").length;
    const totalActiveAlerts = allAlerts.filter(a => a.status === "active").length;

    setStats({
      venues: {
        total: venues.length,
        active: activeVenueIds.size,
        withOpenIncidents: openIncidentVenueIds.size,
        withExpiredDocs: expiredDocVenueIds.size,
        withCriticalAlerts: criticalAlertVenueIds.size,
      },
      kpi: {
        tempCompliance: totalTemps > 0 ? Math.round((correctTemps / totalTemps) * 100) : null,
        cleanCompliance: totalCleaning > 0 ? Math.round((doneCleaning / totalCleaning) * 100) : null,
        verifCompliance: evaluatedVerif > 0 ? Math.round((correctVerif / evaluatedVerif) * 100) : null,
        totalIncidents: incidents.length,
        totalOpenIncidents,
        totalActiveAlerts,
        totalTemps,
        totalCleaning,
      },
      dailyData,
      incidentCategoryData,
      severityData,
      venueComplianceData,
      alertTrend,
    });
    setLoading(false);
  };

  if (venueLoading) return <QuickLoader />;
  if (userRole !== "superadmin") {
    return <EmptyState icon={Shield} title="Accés restringit" description="Analítica global disponible per a superadmins." />;
  }
  if (loading) return <QuickLoader />;
  if (!stats) return null;

  const { venues: v, kpi, dailyData, incidentCategoryData, severityData, venueComplianceData, alertTrend } = stats;

  const avgCompliance = [kpi.tempCompliance, kpi.cleanCompliance, kpi.verifCompliance]
    .filter(x => x !== null);
  const globalCompliance = avgCompliance.length > 0
    ? Math.round(avgCompliance.reduce((a, b) => a + b, 0) / avgCompliance.length)
    : null;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Analítica AEH Reus"
        subtitle="Visió global de la plataforma"
        actions={
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />

      {/* Global compliance score */}
      <div className="bg-gradient-to-br from-[#7B2D3F] to-[#9B4D5F] rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/70 text-sm font-medium">Índex de Compliment Global</p>
            <p className="text-5xl font-bold mt-1">
              {globalCompliance !== null ? `${globalCompliance}%` : "—"}
            </p>
            <p className="text-white/60 text-xs mt-1">Mitjana de temperatures, neteja i verificacions</p>
          </div>
          <BarChart3 className="w-16 h-16 text-white/20" />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 border-t border-white/20 pt-4">
          <MiniKpi label="Temperatures" value={kpi.tempCompliance} />
          <MiniKpi label="Neteja" value={kpi.cleanCompliance} />
          <MiniKpi label="Verificacions" value={kpi.verifCompliance} />
        </div>
      </div>

      {/* Venue KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <KpiCard icon={Building2} label="Total locals" value={v.total} bg="bg-white" color="text-gray-900" iconColor="text-[#7B2D3F]" iconBg="bg-[#7B2D3F]/10" />
        <KpiCard icon={CheckCircle2} label="Locals actius" value={v.active} bg="bg-emerald-50" color="text-emerald-700" iconColor="text-emerald-600" iconBg="bg-emerald-100" />
        <KpiCard icon={AlertTriangle} label="Amb incidències" value={v.withOpenIncidents} bg="bg-amber-50" color="text-amber-700" iconColor="text-amber-600" iconBg="bg-amber-100" />
        <KpiCard icon={FileText} label="Docs caducats" value={v.withExpiredDocs} bg="bg-purple-50" color="text-purple-700" iconColor="text-purple-600" iconBg="bg-purple-100" />
        <KpiCard icon={Bell} label="Alertes crítiques" value={v.withCriticalAlerts} bg="bg-red-50" color="text-red-700" iconColor="text-red-600" iconBg="bg-red-100" />
        <KpiCard icon={XCircle} label="Incidències obertes" value={kpi.totalOpenIncidents} bg="bg-orange-50" color="text-orange-700" iconColor="text-orange-600" iconBg="bg-orange-100" />
      </div>

      {/* Daily registrations chart */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#7B2D3F]" /> Registres diaris (tots els locals)
        </h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={dailyData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="temps" name="Temperatures" stroke="#3B82F6" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="cleaning" name="Neteja" stroke="#10B981" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="incidents" name="Incidències" stroke={BRAND} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Compliance per venue */}
      {venueComplianceData.length > 0 && (
        <Card className="p-5 border-0 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#7B2D3F]" /> Compliment per local
          </h3>
          <ResponsiveContainer width="100%" height={Math.max(160, venueComplianceData.length * 40)}>
            <BarChart
              data={venueComplianceData}
              layout="vertical"
              margin={{ top: 0, right: 40, left: 10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
              <Tooltip
                formatter={(v, n, p) => [`${v}%`, "Compliment"]}
                labelFormatter={(l) => venueComplianceData.find(d => d.name === l)?.fullName || l}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Bar dataKey="compliment" name="Compliment" fill={BRAND} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Incidents by category */}
        {incidentCategoryData.length > 0 && (
          <Card className="p-5 border-0 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Incidències per categoria
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={incidentCategoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {incidentCategoryData.map((_, i) => (
                    <Cell key={`_-${i}`} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Severity breakdown */}
        {severityData.some(s => s.value > 0) && (
          <Card className="p-5 border-0 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" /> Gravetat de les incidències
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={severityData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="value" name="Incidències" radius={[6, 6, 0, 0]}>
                  {severityData.map((entry, i) => (
                    <Cell key={`entry-${i}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Alert trend */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-4 h-4 text-red-500" /> Tendència d'alertes generades
        </h3>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={alertTrend} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={Math.floor(alertTrend.length / 6)} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Bar dataKey="alertes" name="Alertes" fill="#EF4444" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Venue detail table */}
      {venueComplianceData.length > 0 && (
        <Card className="p-5 border-0 shadow-sm overflow-x-auto">
          <h3 className="font-semibold text-gray-900 mb-4">Detall per local</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs text-gray-500 font-medium pb-2">Local</th>
                <th className="text-center text-xs text-gray-500 font-medium pb-2">Temperatures</th>
                <th className="text-center text-xs text-gray-500 font-medium pb-2">Neteja</th>
                <th className="text-center text-xs text-gray-500 font-medium pb-2">Verificacions</th>
                <th className="text-center text-xs text-gray-500 font-medium pb-2">Incidències</th>
                <th className="text-center text-xs text-gray-500 font-medium pb-2">Alertes</th>
              </tr>
            </thead>
            <tbody>
              {venueComplianceData.map((vd, i) => (
                <tr key={`vd-${i}`} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-2.5 font-medium text-gray-800 max-w-[120px] truncate">{vd.fullName}</td>
                  <td className="text-center py-2.5"><CompliancePill value={vd.tempCompliment} /></td>
                  <td className="text-center py-2.5"><CompliancePill value={vd.neteja} /></td>
                  <td className="text-center py-2.5"><CompliancePill value={vd.compliment} /></td>
                  <td className="text-center py-2.5">
                    {vd.incidencies > 0
                      ? <span className="text-amber-600 font-semibold">{vd.incidencies}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="text-center py-2.5">
                    {vd.alertes > 0
                      ? <span className="text-red-600 font-semibold">{vd.alertes}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-2xl font-bold text-white">{value !== null ? `${value}%` : "—"}</p>
      <p className="text-white/60 text-xs">{label}</p>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, bg, color, iconColor, iconBg }) {
  return (
    <div className={`${bg} rounded-2xl p-4 shadow-sm`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs text-gray-500 leading-tight">{label}</p>
        </div>
      </div>
    </div>
  );
}

function CompliancePill({ value }) {
  if (value === null) return <span className="text-gray-300 text-xs">—</span>;
  const color = value >= 90 ? "bg-emerald-100 text-emerald-700" : value >= 70 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  return <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${color}`}>{value}%</span>;
}

const INCIDENT_CATEGORY_LABELS = {
  food_safety: "Seguretat alimentària",
  equipment_failure: "Avaria equips",
  temperature_deviation: "Desviació temperatura",
  cleaning_issue: "Neteja",
  supplier_issue: "Proveïdors",
  staff_issue: "Personal",
  other: "Altres",
};

const SEVERITY_LABELS = {
  low: "Baixa", medium: "Mitjana", high: "Alta", critical: "Crítica",
};

const SEVERITY_COLORS = {
  low: "#10B981", medium: "#F59E0B", high: "#EF4444", critical: "#7F1D1D",
};